import { Link } from '@inertiajs/react'
import { Mail } from 'lucide-react'
import useFeature from '../../../lib/useFeature'
import { useI18n } from '../i18n'
import Brand from './Brand'
import { surfaceUrl } from './surface-urls'

// Cross-surface app links, host-relative so they resolve in every environment.
// Each bare host is the app's dashboard, which redirects to that app's own login
// when there's no session (so the footer admin link is all staff need).
const PORTAL_URL = surfaceUrl('portal')
const OPERATOR_URL = surfaceUrl('admin')

// lucide-react v1 dropped brand glyphs, so the social marks are inlined to keep
// the footer faithful to the mockup. Sized via `.footer-social svg` in CSS.
function LinkedinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.22.79 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
    </svg>
  )
}
function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  )
}

/** Site footer — mirrors `buildFooter` in mockups/marketing/site.js. */
export default function Footer() {
  const { t } = useI18n()
  // Hide links to feature-gated public pages when their module is off (§6.20).
  const quoteEnabled = useFeature('module.quote_requests')
  const trackEnabled = useFeature('module.public_tracking')

  return (
    <footer className="footer">
      <div className="wrap">
        <div className="footer-top">
          <div>
            <Brand />
            <p className="footer-blurb">{t('foot.tagline')}</p>
            <div className="footer-social">
              <a href="https://www.linkedin.com/company/geo4logistics" aria-label="LinkedIn">
                <LinkedinIcon />
              </a>
              <a href="https://twitter.com/geo4logistics" aria-label="Twitter">
                <XIcon />
              </a>
              <a href="mailto:hello@geo4logistics.com" aria-label={t('a11y.email')}>
                <Mail />
              </a>
            </div>
          </div>
          <div>
            <h4>{t('foot.services')}</h4>
            <ul>
              <li>
                <Link href="/services#ocean">{t('foot.ocean')}</Link>
              </li>
              <li>
                <Link href="/services#air">{t('foot.air')}</Link>
              </li>
              <li>
                <Link href="/services#rail">{t('foot.rail')}</Link>
              </li>
              <li>
                <Link href="/services#road">{t('foot.road')}</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4>{t('foot.company')}</h4>
            <ul>
              <li>
                <Link href="/about">{t('foot.about')}</Link>
              </li>
              {quoteEnabled && (
                <li>
                  <Link href="/quote">{t('nav.quote')}</Link>
                </li>
              )}
              {trackEnabled && (
                <li>
                  <Link href="/track">{t('foot.track')}</Link>
                </li>
              )}
              <li>
                <Link href="/contact">{t('foot.contact')}</Link>
              </li>
              <li>
                <Link href="/contact#careers">{t('foot.careers')}</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4>{t('foot.support')}</h4>
            <ul>
              <li>
                <Link href="/help">{t('foot.help')}</Link>
              </li>
              <li>
                <a href={PORTAL_URL}>{t('foot.portal')}</a>
              </li>
              <li>
                <a href={`${PORTAL_URL}/developers`}>{t('foot.devs')}</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="footer-bot">
          <span>{t('foot.rights')}</span>
          <div className="links">
            <Link href="/privacy">{t('foot.privacy')}</Link>
            <Link href="/terms">{t('foot.terms')}</Link>
            <Link href="/cookies">{t('foot.cookies')}</Link>
            <a href={OPERATOR_URL}>{t('foot.admin')}</a>
          </div>
        </div>
      </div>
    </footer>
  )
}
